import { dayjs } from "@/core/utils/date";
import {
  currencyOf,
  DEFAULT_CURRENCY,
  formatMoney,
} from "@/profitability/currency";
import { isMissing, isNumericType, MISSING } from "@/profitability/missing";
import type { ReportColumn, ReportValue } from "@/profitability/types";
import { formatSummaryValue } from "@/profitability/utils";

export const columnLabel = (column: ReportColumn) =>
  column.label ?? column.key.replaceAll("_", " ");
/**
 * What a chart's value is called where the reader hovers it: the block's own
 * label when the script gave one, otherwise the column's, written as a heading
 * rather than as the key it falls back to (`profit` reads "Profit").
 */
export const measureLabel = (column: ReportColumn, label?: string) => {
  const text = label ?? columnLabel(column);
  return text.charAt(0).toUpperCase() + text.slice(1);
};
export const isBlank = (value: ReportValue | undefined) =>
  value == null || (typeof value === "string" && !value.trim());

export const formatReportValue = (
  value: ReportValue | undefined,
  column: ReportColumn,
  /** The report's currency (`useReportCurrency`). */
  currency: string = DEFAULT_CURRENCY,
) => {
  // A blank flag is a row that is fine, not a figure that is missing.
  if (column.flag && isBlank(value)) return "";
  // A figure with no value is missing, never zero (FIN-004): render the
  // result through `showValue` so it reads as "Missing" to a screen reader.
  if (isNumericType(column.type) && isMissing(value)) return MISSING;
  if (value == null) return "Not available";
  if (column.type === "currency" && typeof value === "number")
    return formatMoney(value, currency, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  if (column.type === "boolean") return value ? "Yes" : "No";
  if (column.type === "date" && typeof value === "string") {
    const date = dayjs(value);
    return date.isValid() ? date.format("MMM D, YYYY") : value;
  }
  return formatSummaryValue(
    {
      value: typeof value === "boolean" ? String(value) : value,
      type: column.type,
    },
    currency,
  );
};
export const formatFigureValue = (
  value: ReportValue | undefined,
  column: ReportColumn,
  currency: string = DEFAULT_CURRENCY,
) =>
  column.type === "currency" && typeof value === "number"
    ? formatMoney(value, currency)
    : formatReportValue(value, column, currency);

/**
 * Short form for a chart axis, for example $4.0K. Tooltips and tables still
 * show the exact figure, so nothing is lost by keeping the axis narrow.
 */
export const compactReportValue = (
  value: number,
  column: ReportColumn,
  currency: string = DEFAULT_CURRENCY,
) => {
  if (column.type === "percent")
    return value.toLocaleString(undefined, {
      style: "percent",
      maximumFractionDigits: 1,
    });
  return value.toLocaleString(undefined, {
    notation: "compact",
    maximumFractionDigits: 1,
    ...(column.type === "currency"
      ? { style: "currency", currency: currencyOf(currency) }
      : {}),
  });
};
